import {DialogConfig, DialogLayer, VisibleDialog} from "./types";

/**
 * Centralised layering. Components never hardcode a z-index — they read it from
 * the `VisibleDialog` the orchestrator hands them.
 *
 * Strategy: each layer owns a 1000-wide band. Within a band, dialogs are spaced
 * by `STEP` in the order they were stacked, so a later dialog of the same layer
 * sits above an earlier one — while a higher layer (e.g. CRITICAL confirm) is
 * always above a lower one (e.g. MARKETING), no matter the timing.
 */
export class ZIndexManager {
    private readonly base: number;
    private readonly bandWidth: number;
    private static readonly STEP = 10;

    constructor(base = 1000, bandWidth = 1000) {
        this.base = base;
        this.bandWidth = bandWidth;
    }

    /**
     * Resolve the z-index for `config`, given what's already visible.
     *
     * - Explicit `config.zIndex` always wins (escape hatch / legacy).
     * - Otherwise: layer band + an offset that keeps it above same-layer peers.
     */
    allocate(config: DialogConfig, currentlyVisible: VisibleDialog[]): number {
        if (config.zIndex != null) return config.zIndex;

        const layer = config.layer ?? DialogLayer.CONTENT;
        const bandStart = this.base + layer * this.bandWidth;

        const sameLayerTop = currentlyVisible
            .filter((v) => withinBand(v.zIndex, bandStart, this.bandWidth))
            .reduce((max, v) => Math.max(max, v.zIndex), bandStart);

        const next = sameLayerTop + ZIndexManager.STEP;
        // Don't let a runaway stack bleed into the next layer's band.
        return Math.min(next, bandStart + this.bandWidth - ZIndexManager.STEP);
    }

    /** Highest z-index currently on screen (for backdrops / portals). */
    topZIndex(currentlyVisible: VisibleDialog[]): number {
        return currentlyVisible.reduce((max, v) => Math.max(max, v.zIndex), this.base);
    }
}

function withinBand(z: number, start: number, width: number): boolean {
    return z >= start && z < start + width;
}
